import { PlusIcon } from '@radix-ui/react-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import {
	eventEnd,
	formatDayMonth,
	formatWeekday,
	getNextEvent,
	getVenuePinData,
	groupByFestivalDay,
	isEventPassed,
	type ProgramEvent,
} from '#app/domains/program/pure'
import { useOptionalUser } from '#app/utils/user.ts'
import { EventCard } from '../event/EventCard.tsx'
import { VenueNightMap } from './VenueNightMap.tsx'

const WIDE = '(min-width: 1024px)'

const capitalize = (text: string) =>
	text.charAt(0).toUpperCase() + text.slice(1)

/**
 * The festival program: one section per evening with the concerts on a
 * timeline rail, and the night map beside it. On wide screens the map is
 * sticky and follows the concert in the middle of the viewport; on phones it
 * comes after the list and points at the next concert.
 */
export function ProgramSection({
	events,
	now,
	showMap = true,
	myEventIds = [],
	cartEventIds = [],
}: {
	/** concerts sorted by start time */
	events: ProgramEvent[]
	/** the page clock (keeps server and client renders in step) */
	now?: Date
	showMap?: boolean
	/** concerts the visitor holds tickets for */
	myEventIds?: string[]
	/** of those, the ones only in the cart yet */
	cartEventIds?: string[]
}) {
	const user = useOptionalUser()
	const canCreate = user?.roles.some((role) =>
		role.permissions.some(
			(p) =>
				p.action === 'create' && p.entity === 'event' && p.access === 'any',
		),
	)
	const days = groupByFestivalDay(events)
	const myEvents = useMemo(
		() => events.filter((event) => myEventIds.includes(event.id)),
		[events, myEventIds],
	)
	// the map shows the whole program, or just the visitor's own route
	const [mapScope, setMapScope] = useState<'all' | 'mine'>('all')
	const mapEvents = mapScope === 'mine' && myEvents.length ? myEvents : events
	const pins = useMemo(() => getVenuePinData(mapEvents), [mapEvents])
	const pinNumber = (event: ProgramEvent) =>
		pins.find((pin) => pin.source === event.location?.trim())?.number ?? null
	const next = getNextEvent(events, now ?? new Date())

	const listRef = useRef<HTMLDivElement>(null)
	const [chosen, setChosen] = useState<string | null>(null)
	const [followScroll, setFollowScroll] = useState(false)
	const activeId = chosen ?? next?.id ?? events[0]?.id ?? null

	// wide screens: the map follows whichever concert is mid-screen
	useEffect(() => {
		if (!showMap || typeof window.matchMedia !== 'function') return
		const query = window.matchMedia(WIDE)
		const update = () => setFollowScroll(query.matches)
		update()
		query.addEventListener?.('change', update)
		return () => query.removeEventListener?.('change', update)
	}, [showMap])

	useEffect(() => {
		const list = listRef.current
		if (!followScroll || !list || typeof IntersectionObserver === 'undefined') {
			return
		}
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setChosen(entry.target.getAttribute('data-event-id'))
					}
				}
			},
			{ rootMargin: '-45% 0px -50% 0px' },
		)
		list
			.querySelectorAll('[data-event-id]')
			.forEach((item) => observer.observe(item))
		return () => observer.disconnect()
	}, [followScroll, events])

	return (
		<div className="hk-program" data-map={showMap ? '' : undefined}>
			<div ref={listRef} className="hk-program-list">
				{days.map((day) => (
					<section
						key={day.key}
						className="hk-day"
						aria-labelledby={`hk-day-${day.key}`}
					>
						<h3 id={`hk-day-${day.key}`} className="hk-day-head">
							<span className="hk-day-weekday">
								{capitalize(formatWeekday(day.date))}
							</span>
							<span className="hk-day-date">{formatDayMonth(day.date)}</span>
							<span className="hk-day-count">
								{day.events.length} arrangement
							</span>
						</h3>
						<ol className="hk-rail">
							{day.events.map((event) => (
								<li
									key={event.id}
									className="hk-rail-item"
									data-event-id={event.id}
									data-next={event.id === next?.id ? '' : undefined}
									data-passed={isEventPassed(eventEnd(event)) ? '' : undefined}
									onMouseEnter={() => setChosen(event.id)}
									onFocus={() => setChosen(event.id)}
								>
									<span className="hk-rail-node" aria-hidden="true" />
									<EventCard
										event={event}
										pinNumber={showMap ? pinNumber(event) : null}
										active={followScroll && event.id === activeId}
										mine={myEventIds.includes(event.id)}
										inCart={cartEventIds.includes(event.id)}
									/>
								</li>
							))}
						</ol>
					</section>
				))}
				{events.length === 0 ? (
					<p className="hk-program-empty">
						Programmet er ikkje klart enno — følg med!
					</p>
				) : null}
				{canCreate ? (
					<Link to="/events/new" className="btn btn-outline mt-6">
						Legg til arrangement
						<PlusIcon className="ml-2 h-4 w-4" />
					</Link>
				) : null}
			</div>
			{showMap && events.length ? (
				<div className="hk-program-map">
					{myEvents.length ? (
						<div
							className="hk-map-scope"
							role="group"
							aria-label="Vis på kartet"
						>
							<button
								type="button"
								aria-pressed={mapScope === 'all'}
								onClick={() => setMapScope('all')}
							>
								Heile programmet
							</button>
							<button
								type="button"
								aria-pressed={mapScope === 'mine'}
								onClick={() => setMapScope('mine')}
							>
								Konsertane mine
							</button>
						</div>
					) : null}
					<VenueNightMap
						// a fresh map (and walk) per scope
						key={mapScope}
						events={mapEvents}
						activeEventId={activeId}
						onActivate={setChosen}
					/>
				</div>
			) : null}
		</div>
	)
}
